<template>
  <div class="nft-ui">
    <!-- Loading State -->
    <div v-if="isLoading" class="loading-container">
      <div class="loading-spinner"></div>
      <p>Loading NFTs...</p>
      <div v-if="debugMode" style="font-size: 12px; color: #00ff00; margin-top: 10px;">
        [DEBUG] Showing loading state
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="errorMessage" class="error-container">
      <div class="error-icon">⚠️</div>
      <p>{{ errorMessage }}</p>
      <button @click="retry" class="retry-button">Retry</button>
      <div v-if="debugMode" style="font-size: 12px; color: #00ff00; margin-top: 10px;">
        [DEBUG] Showing error state
      </div>
    </div>

    <!-- NFT Grid -->
    <div v-else class="nft-grid" ref="gridElement">
      <div v-if="debugMode" style="font-size: 12px; color: #00ff00; margin-bottom: 10px;">
        [DEBUG] NFTs to display: {{ nfts.length }}, hasMoreNFTs: {{ hasMoreNFTs }}, isLoading: {{ isLoading }}
      </div>
      
      <div 
        v-for="nft in props.nfts" 
        :key="getNFTKey(nft)"
        class="nft-item"
        @click="handleNFTSelect(nft)"
      >
        <div class="nft-image-container">
          <img 
            v-if="getNFTImage(nft)" 
            :src="getNFTImage(nft)" 
            :alt="getNFTName(nft)"
            @error="handleImageError"
            class="nft-image"
          />
          <div v-else class="nft-placeholder">
            <span>No Image</span>
          </div>
          <div v-if="isVideo(nft)" class="video-indicator">
            <span>🎥</span>
          </div>
        </div>
        
        <div class="nft-details">
          <h4 class="nft-name">{{ getNFTName(nft) }}</h4>
          <p class="nft-collection">{{ getNFTCollection(nft) }}</p>
          <p class="nft-token-id">#{{ getNFTTokenId(nft) }}</p>
          
          <div v-if="getEstimatedValue(nft)" class="nft-value">
            <span class="value-amount" :class="{ 'error-value': getEstimatedValue(nft)?.reason }">
              {{ getEstimatedValue(nft)?.displayValue }}
            </span>
            <span v-if="getEstimatedValue(nft)?.confidence && !getEstimatedValue(nft)?.reason" 
                  :class="`confidence-badge confidence-${getEstimatedValue(nft)?.confidence}`">
              {{ getEstimatedValue(nft)?.confidence }}
            </span>
            <span v-if="getEstimatedValue(nft)?.reason" class="error-badge">
              Error
            </span>
          </div>
          
          <div v-else class="nft-value-loading">
            <div class="loading-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
            <span class="loading-text">Price loading...</span>
          </div>
          
          <div v-if="getEstimatedValue(nft)?.source" class="nft-source">
            <small>{{ getEstimatedValue(nft)?.source }}</small>
          </div>
        </div>
      </div>

      <!-- Load More Indicator -->
      <div v-if="shouldShowLoadMore" class="load-more-container">
        <button @click="loadMore" class="load-more-button">
          Load More NFTs
        </button>
        <div v-if="debugMode" style="font-size: 12px; color: #00ff00; text-align: center; margin-top: 5px;">
          [DEBUG] hasMoreNFTs: {{ hasMoreNFTs }}, isLoading: {{ isLoading }}
        </div>
      </div>
      
      <!-- Done Loading Message -->
      <div v-else-if="!isLoading && nfts.length > 0" class="done-loading-container">
        <div class="done-loading-message">
          ✓ Done loading ({{ nfts.length }} NFTs)
        </div>
        <div v-if="debugMode" style="font-size: 12px; color: #00ff00; text-align: center; margin-top: 5px;">
          [DEBUG] hasMoreNFTs: {{ hasMoreNFTs }}, isLoading: {{ isLoading }}
        </div>
      </div>
      
      <!-- No NFTs Message -->
      <div v-else-if="!isLoading && nfts.length === 0" class="no-nfts-container">
        <div class="no-nfts-message">
          No NFTs found
        </div>
        <div v-if="debugMode" style="font-size: 12px; color: #00ff00; text-align: center; margin-top: 5px;">
          [DEBUG] No NFTs to display
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import type { NFT, EstimatedValue, FloorPriceResponse } from '../types/nft'

// Props
interface Props {
  nfts: NFT[]
  isLoading?: boolean
  hasMoreNFTs?: boolean
  floorPriceData?: FloorPriceResponse | null
  salesData?: any | null
  debugMode?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  nfts: () => [],
  isLoading: false,
  hasMoreNFTs: true,
  floorPriceData: null,
  salesData: null,
  debugMode: false
})

console.log('[NFT UI] Props received:', { nftsLength: props.nfts.length, hasMoreNFTs: props.hasMoreNFTs, isLoading: props.isLoading, debugMode: props.debugMode })

// Emits
const emit = defineEmits<{
  nftSelect: [nft: NFT]
  loadMore: []
  retry: []
}>()

// Reactive state
const gridElement = ref<HTMLElement | null>(null)
const errorMessage = ref<string>('')
const visibleNFTs = ref<NFT[]>([])
const scrollHandler = ref<((event: Event) => void) | null>(null)

// Debug function
const debug = (...args: any[]) => {
  if (props.debugMode) {
    console.log('[NFT UI]', ...args)
  }
}

// Watch for NFT changes
watch(() => props.nfts, (newNFTs) => {
  debug('NFTs changed in NftUI:', newNFTs.length)
  if (newNFTs.length === 0) {
    debug('WARNING: Received empty NFTs array in NftUI')
  }
}, { deep: true })

// Watch for hasMoreNFTs changes
watch(() => props.hasMoreNFTs, (newValue) => {
  debug('hasMoreNFTs changed in NftUI:', newValue)
})

// Watch for isLoading changes
watch(() => props.isLoading, (newValue) => {
  debug('isLoading changed in NftUI:', newValue)
  if (!newValue && errorMessage.value) {
    errorMessage.value = ''
  }
})

// Computed
const shouldShowLoadMore = computed(() => {
  const shouldShow = props.hasMoreNFTs && !props.isLoading
  debug('shouldShowLoadMore computed:', shouldShow, 'hasMoreNFTs:', props.hasMoreNFTs, 'isLoading:', props.isLoading)
  return shouldShow
})

// Methods
const getTokenId = (nft: NFT): string => {
  if (typeof nft.id === 'string') {
    return nft.id
  } else if (nft.id && typeof nft.id === 'object' && 'tokenId' in nft.id) {
    return nft.id.tokenId
  } else {
    return nft.tokenId || ''
  }
}

const getNFTKey = (nft: NFT): string => {
  const contractAddress = nft.contract?.address || nft.contractAddress || ''
  const tokenId = getTokenId(nft)
  return `${contractAddress}-${tokenId}`
}

const getNFTImage = (nft: NFT): string => {
  if (!nft) return ''
  
  let mediaUrl = ''
  
  // Try image property first
  if (nft.image) {
    if (typeof nft.image === 'object' && nft.image !== null) {
      mediaUrl = nft.image.cachedUrl || nft.image.originalUrl || nft.image.thumbnailUrl || nft.image.pngUrl || ''
    } else if (typeof nft.image === 'string') {
      mediaUrl = nft.image
    }
  }
  
  // Fallback to media array
  if (!mediaUrl && nft.media && nft.media[0]) {
    mediaUrl = nft.media[0].gateway || nft.media[0].thumbnail || ''
  }

  // Validate URL
  const isLikelyEthAddress = (url: string): boolean => {
    if (typeof url !== 'string') return false
    if (url.startsWith('0x') && (url.length === 42 || url.length === 40)) {
      return true
    }
    if (url.includes('ipfs/0x') || url.includes('ipfs%2F0x')) {
      const match = url.match(/ipfs[\/%]0x[a-fA-F0-9]{40,42}/)
      return !!match
    }
    return false
  }

  if (isLikelyEthAddress(mediaUrl)) {
    return ''
  } else if (mediaUrl && !mediaUrl.match(/^https?:\/\//) && !mediaUrl.startsWith('data:') && !mediaUrl.startsWith('ipfs://')) {
    return ''
  }

  return mediaUrl
}

const getNFTName = (nft: NFT): string => {
  return nft.name || nft.title || `#${(nft.id && typeof nft.id === 'object' ? nft.id?.tokenId : nft.id) || nft.tokenId || ''}`
}

const getNFTCollection = (nft: NFT): string => {
  return nft.contractMetadata?.name || nft.contract?.name || 'Unnamed Collection'
}

const getNFTTokenId = (nft: NFT): string => {
  return getTokenId(nft)
}

const isVideo = (nft: NFT): boolean => {
  const mediaUrl = getNFTImage(nft)
  return !!mediaUrl && (
    mediaUrl.includes('.mp4') ||
    mediaUrl.includes('.webm') ||
    mediaUrl.includes('.mov') ||
    mediaUrl.includes('video/')
  )
}

const getEstimatedValue = (nft: NFT): EstimatedValue | null => {
  // If NFT already has estimated value, use it
  if (nft.estimatedValue) {
    return nft.estimatedValue
  }

  // Calculate estimated value based on floor price and sales data
  if (!props.floorPriceData && !props.salesData) {
    return null
  }

  let estimatedValue: EstimatedValue | null = null

  if (props.floorPriceData) {
    // Handle floor price data with error checking
    if (props.floorPriceData.error) {
      estimatedValue = {
        value: null,
        currency: 'ETH',
        confidence: 'low',
        source: `${props.floorPriceData.marketplace} Floor Price`,
        reason: props.floorPriceData.error,
        displayValue: `Error: ${props.floorPriceData.error}`
      }
    } else if (props.floorPriceData.floorPrice !== null && props.floorPriceData.floorPrice !== undefined) {
      // Valid floor price data
      const marketplaceName = props.floorPriceData.marketplace === 'looksrare' ? 'LooksRare' : 
                            props.floorPriceData.marketplace === 'opensea' ? 'OpenSea' : 
                            props.floorPriceData.marketplace
      
      estimatedValue = {
        value: props.floorPriceData.floorPrice,
        currency: props.floorPriceData.priceCurrency || 'ETH',
        confidence: 'medium',
        source: `${marketplaceName} Floor Price`,
        displayValue: formatDisplayValue(props.floorPriceData.floorPrice, props.floorPriceData.priceCurrency || 'ETH')
      }
    }
  }

  if (props.salesData && props.salesData.recentSales) {
    const recentSales = props.salesData.recentSales
    const avgPrice = recentSales.reduce((sum: number, sale: any) => sum + sale.price, 0) / recentSales.length
    
    if (!estimatedValue || avgPrice > (estimatedValue.value || 0)) {
      estimatedValue = {
        value: avgPrice,
        currency: 'ETH',
        confidence: 'high',
        source: 'Recent Sales',
        recentSales: recentSales.length,
        displayValue: formatDisplayValue(avgPrice, 'ETH')
      }
    }
  }

  return estimatedValue
}

const formatDisplayValue = (value: number | null, currency: string = 'ETH'): string | null => {
  if (value === null || value === undefined) return null
  
  if (currency === 'ETH') {
    return `${value.toFixed(4)} ETH`
  } else if (currency === 'USD') {
    return `$${value.toLocaleString()}`
  } else {
    return `${value.toFixed(4)} ${currency}`
  }
}

const handleNFTSelect = (nft: NFT) => {
  console.log('[NFT UI] NFT selected:', nft.name || nft.title || 'Untitled NFT')
  console.log('[NFT UI] Emitting nftSelect event with NFT:', nft)
  debug('NFT selected:', nft.name || nft.title)
  emit('nftSelect', nft)
}

const handleImageError = (event: Event) => {
  const img = event.target as HTMLImageElement
  img.style.display = 'none'
  const placeholder = img.parentElement?.querySelector('.nft-placeholder') as HTMLElement
  if (placeholder) {
    placeholder.style.display = 'flex'
  }
}

const loadMore = () => {
  emit('loadMore')
}

const retry = () => {
  errorMessage.value = ''
  emit('retry')
}

const setupScrollListener = () => {
  if (!gridElement.value) return

  scrollHandler.value = (event: Event) => {
    const target = event.target as HTMLElement
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 100) {
      emit('loadMore')
    }
  }

  gridElement.value.addEventListener('scroll', scrollHandler.value)
}

const removeScrollListener = () => {
  if (scrollHandler.value && gridElement.value) {
    gridElement.value.removeEventListener('scroll', scrollHandler.value)
    scrollHandler.value = null
  }
}

// Watchers
watch(() => props.nfts, (newNFTs) => {
  visibleNFTs.value = newNFTs
}, { immediate: true })

watch(() => props.isLoading, (loading) => {
  if (!loading && errorMessage.value) {
    errorMessage.value = ''
  }
})

// Lifecycle
onMounted(() => {
  console.log('[NFT UI] Component mounted, debugMode:', props.debugMode)
  setupScrollListener()
})

onUnmounted(() => {
  removeScrollListener()
})
</script>

<style scoped>
.nft-ui {
  width: 100%;
  height: 100%;
}

.nft-details {
  padding: 12px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  color: var(--text-primary);
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: var(--text-secondary);
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--border-color);
  border-top: 4px solid var(--accent-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  text-align: center;
  color: var(--error-color);
}

.error-icon {
  font-size: 48px;
  margin-bottom: 20px;
}

.retry-button {
  margin-top: 20px;
  padding: 10px 20px;
  background: var(--button-bg);
  color: var(--text-primary);
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
}

.retry-button:hover {
  background: var(--button-hover);
}

.nft-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
  padding: 12px;
  min-height: 100%;
  overflow-y: auto;
}

.nft-item {
  background: var(--card-bg);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px var(--shadow-color, rgba(0, 0, 0, 0.1));
  transition: transform 0.2s, box-shadow 0.2s, background-color 0.3s;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  min-height: 350px;
  height: auto;
  border: 1px solid var(--border-color);
}

.nft-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.nft-image-container {
  position: relative;
  width: 100%;
  height: 200px;
  min-height: 200px;
  overflow: hidden;
  flex-shrink: 0;
}

.nft-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.nft-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--background-secondary);
  color: var(--text-secondary);
  font-size: 0.8em;
}

.video-indicator {
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
}

.nft-details {
  padding: 15px;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.nft-name {
  font-weight: 600;
  margin: 0 0 4px 0;
  font-size: 0.9em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--text-primary);
}

.nft-collection, .nft-token-id {
  color: var(--text-secondary);
  font-size: 0.8em;
  margin: 2px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.nft-value {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 5px;
}

.value-amount {
  font-weight: 600;
  color: var(--text-primary);
  font-size: 14px;
}

.confidence-badge {
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
}

.confidence-high {
  background: var(--confidence-high-bg);
  color: var(--confidence-high-text);
}

.confidence-medium {
  background: var(--confidence-medium-bg);
  color: var(--confidence-medium-text);
}

.confidence-low {
  background: var(--confidence-low-bg);
  color: var(--confidence-low-text);
}

.nft-source {
  font-size: 11px;
  color: var(--text-secondary);
}

.load-more-container {
  grid-column: 1 / -1;
  display: flex;
  justify-content: center;
  padding: 20px;
}

.load-more-button {
  display: block;
  width: 100%;
  padding: 12px;
  background: var(--button-bg);
  border: 1px solid var(--border-color);
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9em;
  color: var(--text-primary);
  transition: background-color 0.2s, border-color 0.2s, color 0.2s;
}

.load-more-button:hover {
  background: var(--button-hover);
  border-color: var(--accent-color);
}

.done-loading-container {
  grid-column: 1 / -1;
  display: flex;
  justify-content: center;
  padding: 20px;
  color: var(--text-secondary);
  font-size: 0.9em;
}

.done-loading-message {
  background: var(--background-secondary);
  padding: 12px 20px;
  border-radius: 6px;
  border: 1px solid var(--border-color);
}

.no-nfts-container {
  grid-column: 1 / -1;
  display: flex;
  justify-content: center;
  padding: 20px;
  color: var(--text-secondary);
  font-size: 0.9em;
}

.no-nfts-message {
  background: var(--background-secondary);
  padding: 12px 20px;
  border-radius: 6px;
  border: 1px solid var(--border-color);
}

/* Responsive Design */
@media (max-width: 768px) {
  .nft-grid {
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 10px;
    padding: 10px;
  }
  
  .nft-image-container {
    height: 150px;
  }
  
  .nft-details {
    padding: 12px;
  }
  
  .nft-name {
    font-size: 14px;
  }
  
  .nft-collection {
    font-size: 12px;
  }
}

@media (max-width: 480px) {
  .nft-grid {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 8px;
    padding: 8px;
  }
}

.nft-value-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 5px;
}

.loading-dots {
  display: flex;
  gap: 2px;
}

.loading-dots span {
  width: 3px;
  height: 3px;
  background: var(--text-secondary);
  border-radius: 50%;
  animation: loading-dots 1.4s infinite ease-in-out;
  opacity: 0.6;
}

.loading-dots span:nth-child(1) {
  animation-delay: -0.32s;
}

.loading-dots span:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes loading-dots {
  0%, 80%, 100% {
    transform: scale(0.8);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

.loading-text {
  font-size: 11px;
  color: var(--text-secondary);
  opacity: 0.8;
}
</style> 